/**
 * ImageUploadInput
 * ────────────────
 * A URL text field paired with an "Upload" button. Non-technical
 * operators can either paste an image URL OR pick a file from their
 * computer — the file is optimized, uploaded to the public
 * `sponsor-creatives` bucket, and the resulting public URL is written
 * back into the same field via `onChange`. No more "where do I get a
 * URL?" for festival hero images and directory listing photos.
 */

import { useRef, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Loader2, Upload } from 'lucide-react';
import { toast } from 'sonner';
import { optimizeImageForUpload, PHOTO_PRESET } from '@/lib/imageOptimize';
import { deleteSponsorCreativeByUrl } from '@/lib/storageCleanup';

const BUCKET = 'sponsor-creatives';
const MAX_BYTES = 8 * 1024 * 1024; // 8 MB
const ACCEPTED = 'image/png,image/jpeg,image/webp';
const ACCEPTED_SET = new Set(['image/png', 'image/jpeg', 'image/webp']);

export interface ImageUploadInputProps {
  value: string;
  onChange: (url: string) => void;
  /** Storage sub-folder, e.g. 'festivals' or 'listings'. */
  folder?: string;
  placeholder?: string;
  disabled?: boolean;
}

export function ImageUploadInput({
  value,
  onChange,
  folder = 'uploads',
  placeholder = 'https://…  or upload a file →',
  disabled = false,
}: ImageUploadInputProps) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [uploading, setUploading] = useState(false);
  // URL of the file THIS component uploaded during the current session.
  // Only session uploads are safe to auto-delete on re-upload — a
  // pre-existing (persisted) value is cleaned up by the parent on save.
  const sessionUploadRef = useRef<string | null>(null);

  const handleFile = async (file: File) => {
    if (!file) return;
    if (!ACCEPTED_SET.has(file.type)) {
      toast.error('Image must be a PNG, JPEG, or WEBP file.');
      return;
    }
    if (file.size > MAX_BYTES) {
      toast.error(`Image is too large (${(file.size / 1024 / 1024).toFixed(1)} MB). Max 8 MB.`);
      return;
    }
    setUploading(true);
    try {
      const optimized = await optimizeImageForUpload(file, PHOTO_PRESET);
      // Read the bytes first. When the optimizer can't decode the source
      // (HEIC, a huge file, or an unreadable iCloud/Photos placeholder that
      // reports a size but yields no data) it returns the ORIGINAL File —
      // which storage-js would then wrap in an empty multipart body that
      // Storage rejects as "No content provided". Loading the buffer here
      // forces the real bytes to materialise, catches the empty case, and
      // guarantees our contentType is honoured on upload.
      let bytes: ArrayBuffer;
      try {
        bytes = await optimized.arrayBuffer();
      } catch {
        bytes = new ArrayBuffer(0);
      }
      if (!bytes.byteLength) {
        toast.error(
          'That image came through empty or unreadable. If it lives in Photos or iCloud, export/download it to your Downloads folder first, then upload that copy.',
        );
        return;
      }
      const ext = optimized.name.split('.').pop()?.toLowerCase() || 'png';
      const contentType = optimized.type || file.type || 'image/png';
      const path = `${folder}/${Date.now()}-${Math.random().toString(36).slice(2, 8)}.${ext}`;
      const { error: uploadErr } = await supabase.storage
        .from(BUCKET)
        .upload(path, bytes, {
          contentType,
          upsert: false,
          cacheControl: '31536000',
        });
      if (uploadErr) throw uploadErr;
      const { data } = supabase.storage.from(BUCKET).getPublicUrl(path);
      // Reclaim the previous session upload we're superseding so repeated
      // re-picks before saving don't leave orphans in the bucket.
      if (sessionUploadRef.current && sessionUploadRef.current !== data.publicUrl) {
        void deleteSponsorCreativeByUrl(sessionUploadRef.current);
      }
      sessionUploadRef.current = data.publicUrl;
      onChange(data.publicUrl);
      toast.success('Image uploaded');
    } catch (err: any) {
      const raw = String(err?.message || '');
      if (/no content provided/i.test(raw)) {
        toast.error(
          'Upload failed: the file arrived empty. If it came from Photos or iCloud, export it to your device first, then upload that copy.',
        );
      } else if (/exceeded the maximum allowed size|payload too large/i.test(raw)) {
        toast.error('That image is over the 8 MB limit.');
      } else {
        toast.error(raw || 'Upload failed');
      }
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  return (
    <div className="flex gap-2">
      <Input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="bg-gray-800 border-gray-700 text-white flex-1"
      />
      <input
        ref={inputRef}
        type="file"
        accept={ACCEPTED}
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) handleFile(f);
        }}
      />
      <Button
        type="button"
        variant="outline"
        disabled={disabled || uploading}
        onClick={() => inputRef.current?.click()}
        className="border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10 hover:text-emerald-200 shrink-0"
      >
        {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
        <span className="ml-1.5 hidden sm:inline">Upload</span>
      </Button>
    </div>
  );
}
